/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

// Port of vpx-js lib/render/threejs/three-light-generator.ts.
// Changes: physical light units (three r165 and later have no legacy units): the intensity
// of a point light is in candela and its decay is 2, so the bulb intensity is retuned.
// This file also holds the scene lights (one soft key light with a shadow, one hemisphere
// light) and the state of each light insert: off, on, or blinking.
// Blink rule: a light never flashes more than three times in one second, and a reduced
// motion setting turns blinking into a steady glow (THEME.md section 11.3).

import {
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  type MeshStandardMaterial,
  PointLight,
} from "three";
import type { LightSpec, LightState } from "../shared/contracts.ts";
import type { PlayfieldBounds } from "./camera.ts";

/** Point light intensity in candela for a bulb at full brightness. The distance is in table units. */
export const BULB_CANDELA = 9000;
/** Reach of a bulb light in table units. */
export const BULB_DISTANCE = 260;
/** A blinking light stays on this long or off this long at the least (3 flashes per second at most). */
export const MIN_BLINK_STEP_MS = 170;
/** A light changes between on and off no more often than this. */
export const MIN_SWITCH_MS = 167;
/** Steady level of a blinking light when reduced motion is on. */
export const REDUCED_BLINK_LEVEL = 0.7;
/**
 * With reduced motion, a lamp that goes off stays lit this long (ms), so a lamp that moves in a
 * sequence (the skill shot lanes step every 500 ms) reads as one steady glow. It is a little
 * more than the full cycle of that sequence (4 steps of 500 ms).
 */
export const REDUCED_HOLD_MS = 2100;
/** Default blink pattern: one step on, one step off. */
export const DEFAULT_BLINK_PATTERN = "10";
/** Default step of a blink pattern in milliseconds. */
export const DEFAULT_BLINK_STEP_MS = 125;
/** Size of the shadow map. */
export const SHADOW_MAP_SIZE = 1024;
/**
 * An unlit insert shows its family colour at this level (0 to 1), so the colour is always
 * visible. A lit insert shows 1, so lit and unlit differ in brightness and not in hue alone. The
 * rest level adds no point light: only a lit lamp lights the playfield around it.
 */
export const REST_LEVEL = 0.28;
/** Emissive gain of the fill of an insert at level 1. */
export const FILL_GAIN = 1.9;
/** Emissive gain of the bulb of an insert at level 1. */
export const BULB_GAIN = 2.4;

/** The scene lights. */
export interface SceneLights {
  readonly group: Group;
  readonly key: DirectionalLight;
  readonly hemisphere: HemisphereLight;
}

/** Make the scene lights for a playfield. The key light casts the shadows. */
export function createSceneLights(bounds: PlayfieldBounds): SceneLights {
  const group = new Group();
  group.name = "lights";
  const cx = (bounds.left + bounds.right) / 2;
  const cz = (bounds.top + bounds.bottom) / 2;
  const span = Math.max(bounds.right - bounds.left, bounds.bottom - bounds.top);

  const hemisphere = new HemisphereLight(0xffffff, 0x444444, 1.1);
  hemisphere.name = "hemisphere";
  group.add(hemisphere);

  const key = new DirectionalLight(0xffffff, 2.6);
  key.name = "key";
  key.position.set(cx - span * 0.35, span * 0.7, cz + span * 0.25);
  key.target.position.set(cx, 0, cz);
  key.castShadow = true;
  key.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  const half = span * 0.62;
  key.shadow.camera.left = -half;
  key.shadow.camera.right = half;
  key.shadow.camera.top = half;
  key.shadow.camera.bottom = -half;
  key.shadow.camera.near = 10;
  key.shadow.camera.far = span * 2.2;
  key.shadow.bias = -0.0008;
  key.shadow.normalBias = 0.6;
  key.shadow.radius = 3;
  group.add(key);
  group.add(key.target);
  return { group, key, hemisphere };
}

/** Level of a blink pattern at a time: 1 for a step marked "1", else 0. */
export function blinkLevel(pattern: string, stepMs: number, timeMs: number): 0 | 1 {
  const text = pattern.length > 0 ? pattern : DEFAULT_BLINK_PATTERN;
  const step = Math.max(
    Number.isFinite(stepMs) ? stepMs : DEFAULT_BLINK_STEP_MS,
    MIN_BLINK_STEP_MS,
  );
  const index = Math.floor(Math.max(timeMs, 0) / step) % text.length;
  return text[index] === "1" ? 1 : 0;
}

/** The runtime state of one light insert. */
export class InsertState {
  readonly name: string;
  readonly color: Color;
  readonly pattern: string;
  readonly stepMs: number;
  readonly baseIntensity: number;
  readonly fill: MeshStandardMaterial;
  readonly bulb: MeshStandardMaterial | null;
  readonly point: PointLight | null;
  /** State from the last "light" event. */
  state: LightState;
  /** Intensity from an item frame. It wins over the state. An event clears it. */
  override: number | undefined;
  /** Intensity that the renderer applied last, from the state (the accent is not in it). */
  shown = 0;
  /**
   * Extra glow from the effects (a hit, a chase, a wash), from 0 to 1. The lamp shows the larger
   * of the state and the accent. The effects write it. The state machine never reads it.
   */
  accent = 0;
  /** A colour that replaces the lamp colour while it is set (a mode wash). Null: no change. */
  tint: Color | null = null;
  private level: 0 | 1 = 0;
  private heldLevel = 0;
  private litAt = Number.NEGATIVE_INFINITY;
  private switchedAt = Number.NEGATIVE_INFINITY;

  constructor(
    spec: LightSpec,
    fill: MeshStandardMaterial,
    bulb: MeshStandardMaterial | null,
    point: PointLight | null,
  ) {
    this.name = spec.name;
    this.color = new Color(spec.color);
    this.pattern = spec.rgBlinkPattern ?? DEFAULT_BLINK_PATTERN;
    this.stepMs = spec.blinkInterval ?? DEFAULT_BLINK_STEP_MS;
    this.baseIntensity = spec.intensity ?? 1;
    this.fill = fill;
    this.bulb = bulb;
    this.point = point;
    this.state = spec.state ?? 0;
    this.override = undefined;
  }

  /**
   * The target intensity (0..1) for a game time, before the flash limit. With reduced motion the
   * lamp follows its state only: the fades and the blinks of an item frame do not show.
   */
  target(timeMs: number, reducedMotion: boolean): number {
    if (reducedMotion) return this.steadyTarget();
    if (this.override !== undefined) return Math.min(1, Math.max(0, this.override));
    if (this.state === 0) return 0;
    if (this.state === 1) return 1;
    return blinkLevel(this.pattern, this.stepMs, timeMs);
  }

  /** The steady intensity of the state: off, on, or the glow of a blinking lamp. */
  private steadyTarget(): number {
    if (this.state === 0) return 0;
    return this.state === 1 ? 1 : REDUCED_BLINK_LEVEL;
  }

  /** With reduced motion, keep a lamp that went off lit for REDUCED_HOLD_MS. */
  private hold(wanted: number, timeMs: number, reducedMotion: boolean): number {
    if (!reducedMotion) return wanted;
    if (wanted > 0) {
      this.heldLevel = wanted;
      this.litAt = timeMs;
      return wanted;
    }
    const age = timeMs - this.litAt;
    return age >= 0 && age < REDUCED_HOLD_MS ? this.heldLevel : 0;
  }

  /** Work out the intensity to show. A switch between on and off waits for MIN_SWITCH_MS. */
  resolve(timeMs: number, reducedMotion: boolean): number {
    const wanted = this.hold(this.target(timeMs, reducedMotion), timeMs, reducedMotion);
    const wantedLevel: 0 | 1 = wanted > 0.5 ? 1 : 0;
    if (wantedLevel !== this.level) {
      if (timeMs - this.switchedAt >= MIN_SWITCH_MS || timeMs < this.switchedAt) {
        this.level = wantedLevel;
        this.switchedAt = timeMs;
      } else {
        // Too soon after the last switch: keep the old look.
        return this.shown;
      }
    }
    return wanted;
  }

  /** Resolve and apply to the materials and the point light. Returns the intensity shown. */
  apply(timeMs: number, reducedMotion: boolean): number {
    this.shown = this.resolve(timeMs, reducedMotion);
    const intensity = Math.max(this.shown, Math.min(1, Math.max(0, this.accent)));
    const color = this.tint ?? this.color;
    // The materials never go below the rest level, so an unlit insert keeps its colour.
    const glow = Math.max(intensity, REST_LEVEL);
    this.fill.emissive.copy(color);
    this.fill.emissiveIntensity = glow * FILL_GAIN;
    if (this.bulb) {
      this.bulb.emissive.copy(color);
      this.bulb.emissiveIntensity = glow * BULB_GAIN;
    }
    if (this.point) {
      this.point.color.copy(color);
      this.point.intensity = BULB_CANDELA * this.baseIntensity * intensity;
    }
    return intensity;
  }
}

/** Make the point light of a bulb. The decay is 2 (physical). */
export function createBulbLight(spec: LightSpec): PointLight {
  const light = new PointLight(spec.color, 0, BULB_DISTANCE, 2);
  light.name = `bulb-light-${spec.name}`;
  return light;
}
